// Patterns that flag content as inappropriate for a kids' inclusion site.
// We check normalized text (lowercase, common letter substitutions resolved)
// so simple misspellings and leet-speak don't slip through.

const BAD_PATTERNS = [
  // Slurs and hate language (abbreviated to avoid embedding them verbatim)
  /\bn[i!1][g9][g9][ae3]r/i,
  /\bf[a@]g+[o0]t/i,
  /\bc[u*][n*][t*]/i,
  /\bretard/i,
  /\bspastic/i,
  /\bcripp+le/i,
  /\bm[o0]r[o0]n/i,
  /\bimbecil/i,
  /\bidiot/i,
  /\bstupid/i,
  /\bdumb/i,
  /\bfreak/i,
  /\bw[e3][i1]rd[o0]/i,
  /\bloser/i,
  /\bbroke[n]?\s+brain/i,
  /\babn[o0]rmal/i,

  // Ableist language specifically harmful to this community
  /\bveg[e3]table/i,          // used as a slur for severely disabled people
  /\bdrool[ie]*/i,
  /\bhandicap+ed/i,           // when used mockingly (we allow educational use)

  // General profanity
  /\bf+[u*][c*][k*]/i,
  /\bs+h+[i!1]+t+/i,
  /\ba+[s$][s$]+h+[o0]l/i,
  /\bb[i!1][t*][c*]h/i,
  /\bd[a@]mn/i,
  /\bh[e3]ll/i,
  /\bcr[a@]p/i,
  /\bpiss/i,

  // Bullying / threatening language
  /\bkill\s+(your?self|them|you)/i,
  /\bdie\s+(loser|freak|already)/i,
  /\bnob[o0]dy\s+(likes?|wants?)\s+you/i,
  /\bgo\s+away/i,
  /\byou('re|\s+are)\s+(ugly|gross|disgusting)/i,
  /\bhate\s+you/i,
  /\bshut\s+up/i,
];

// Phrases that are mean-spirited even without slurs
const MEAN_PHRASES = [
  /they('re|\s+are)\s+not\s+(normal|real|human)/i,
  /should(n't|'t)?\s+(be\s+allowed|exist)/i,
  /waste\s+of\s+(space|time|money)/i,
  /\bfaking\s+it\b/i,
  /not\s+really\s+disabled/i,
];

/**
 * Returns { ok: boolean, reason: string | null }
 * reason is a friendly, kid-appropriate message explaining what went wrong.
 */
export function moderateText(text) {
  // Normalize: lowercase, collapse whitespace, resolve common substitutions
  const normalized = text
    .toLowerCase()
    .replace(/[0-9]/g, c => ({ 0: 'o', 1: 'i', 3: 'e', 4: 'a', 5: 's', 8: 'b', 9: 'g' }[c] || c))
    .replace(/[@]/g, 'a')
    .replace(/[$]/g, 's')
    .replace(/[!]/g, 'i')
    .replace(/[*]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  for (const pattern of BAD_PATTERNS) {
    if (pattern.test(normalized)) {
      return {
        ok: false,
        reason: "Oops! It looks like your message contains a word that isn't kind. This is a safe space for everyone — please rewrite your story using kind, respectful words. 💜",
      };
    }
  }

  for (const pattern of MEAN_PHRASES) {
    if (pattern.test(normalized)) {
      return {
        ok: false,
        reason: "Hmm, part of your message might come across as unkind. Remember, this website celebrates every child! Please try again with a kinder message. 🌟",
      };
    }
  }

  // Length sanity check
  if (text.trim().length < 20) {
    return { ok: false, reason: "Please write a little more — at least 20 characters! 📝" };
  }

  if (text.trim().length > 1000) {
    return { ok: false, reason: "Your story is too long! Please keep it under 1,000 characters. ✂️" };
  }

  return { ok: true, reason: null };
}
